<template>
  <div class="contents px-csv">
    <h2>PX連携 勤怠CSVダウンロード</h2>
    <div class="container">
      <template v-if="isLoading">
        <template v-if="!isError">
          <loading :isLoading="isLoading" :message="'読み込み中...'"></loading>
        </template>
      </template>
      <template v-else>
        <div class="flex">
          <div class="baseinfo control"><!-- baseinfo -->

            <div class="period-selecter">
              <div class="item ">
                <p>締め年月</p>
                <month-picker v-model="yearMonthForMonthPicker" @update:modelValue="changeMonth"  :error="getError(form, 'year') || getError(form, 'month')" 
                :disabled="isLoading" :clearable=false></month-picker>
              </div>

              <div class="item ">
                <p>締め日</p>
                <input-text :type="'number'" v-model="shimeDay" inputmode="numeric" class="small" :min="1" :max="31"
                  v-on:change="changeShimeDay" :error="getError(form, 'shime')"></input-text>
              </div>
            </div>

          </div><!-- /baseinfo -->

          <div class="maininfo flex">
            <div class="btn-container">
              <button type="button" class="btn success" @click="checkShimeStatus" :disabled="isLoading || isCreating">
                <template v-if="isCreating">
                  <i class="fa-solid fa-spinner fa-spin-pulse"></i>勤務承認の確定状況を確認中…
                </template>
                <template v-else>
                  <i class="fa-solid fa-floppy-disk"></i>勤務承認の確定状況を確認する
                </template>
              </button>
            </div>

            <div class="btn-container">
              <button type="button" class="btn success" @click="dlCsv"
                :disabled="isLoading || isCreating">
                <template v-if="isCreating">
                  <i class="fa-solid fa-spinner fa-spin-pulse"></i>CSVダウンロード中…
                </template>
                <template v-else>
                  <i class="fa-solid fa-floppy-disk"></i>CSVダウンロード
                </template>
              </button>
            </div>

          </div><!-- /maininfo -->
        </div>

        <div v-if="isChecked && !isListError">
          <template v-if="isListChecking">
            <loading :isLoading="isLoading" :message="'処理中...'"></loading>
          </template>
          <template v-if="canDownLoad && !isListChecking">
            <h3 class="info">勤務承認に未確定のデータはありません</h3>
          </template>
          <template v-if="!canDownLoad && !isListChecking">
            <h3><i class="fa-solid fa-circle-exclamation"></i> 勤務承認に未確定のデータがあります</h3>
            <!-- 打刻 -->
            <div>
              <h3>否認または未承認の勤務日</h3>
              <scroll-table v-if="checkListInfo.dakoku.length > 0">
                <table>
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>ユーザー名</th>
                      <th>就業場所</th>
                      <th>打刻日</th>
                      <th>承認</th>
                      <th>承認ユーザー名</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in checkListInfo.dakoku" :key="row.dakoku_id">
                      <td>{{ index + 1 }}</td>
                      <td>{{ row.staff_name }}</td>
                      <td>{{ row.syuugyou_basyo_name }}</td>
                      <td>{{ dateFormat(row.dakoku_date) }}</td>
                      <td>{{ syouninStatus(row.syounin_status_type) }}</td>
                      <td>{{ row.syounin_user_name }}</td>
                    </tr>
                  </tbody>
                </table>
              </scroll-table>
              <p v-else>該当する勤務日はありません</p>
            </div>
            <!-- /打刻 -->

            <!-- 勤怠サマリー -->
            <!-- <div> -->
              <h3>月の締めが確定されていない勤務集計</h3>
              <scroll-table v-if="checkListInfo.summary.length > 0">
                <table>
                  <thead>
                    <tr>
                      <th>&nbsp;</th>
                      <th>ユーザー名</th>
                      <th>就業場所</th>
                      <th>期間</th>
                      <th>承認ユーザー名</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in checkListInfo.summary" :key="row.kintai_summary_id">
                      <td>{{ index + 1 }}</td>
                      <td>{{ row.staff_name }}</td>
                      <td>{{ row.syuugyou_basyo_name }}</td>
                      <td>{{ dateFormat(checkListInfo.start) }}～{{ dateFormat(checkListInfo.end) }}</td>
                      <td>{{ row.syounin_user_name }}</td>
                    </tr>
                  </tbody>
                </table>
              </scroll-table>
              <p v-else>該当する勤務集計はありません</p>
            <!-- </div> -->
            <!-- /勤怠サマリー -->
          </template>

        </div>

      </template>

    </div>

  </div>
</template>

<script lang="ts">

import { reactive, computed, defineComponent, ref } from 'vue'
import { useInit } from '@/composable/px/init';
import { useList } from '@/composable/px/list';
import { useCreate } from '@/composable/px/create';
import { RouterConst } from '@/const/Router';
import { MonthPickerModelValue } from "../../form/MonthPicker.vue";
import { dateFormat } from '@/filters/common';
import { useForm } from '@/composable/form/form';

export default defineComponent({
  setup() {
    // 初期化
    const { form, isLoading, isError } = useInit();

    const { isListChecking, isListError, getList, syouninStatus, checkListInfo } = useList();

    const { isCreating, downloadKintaiCSV } = useCreate();

    const { getError } = useForm();

    const isChecked = ref(false);
    const canDownLoad = ref(false);
    const yearMonth = ref('');

    // PX出力の年月指定用
    let year = ref(new Date().getFullYear()); // 初期値として現在の西暦をセット
    let month = ref(new Date().getMonth() + 1); // 初期値として現在の月をセット

    // MonthPickerに渡すための年月obj
    const yearMonthForMonthPicker = computed((): MonthPickerModelValue => {
      return {
        year: year.value,
        month: month.value - 1 // プログラム上の月番号と表示上の月名の補正
      }
    });

    // MonthPickerで年月を決定した際に実行
    const changeMonth = async (val: MonthPickerModelValue) => {
      month.value = val.month + 1 // プログラム上の月番号と表示上の月名の補正
      year.value = val.year

      canDownLoad.value = false;
      isChecked.value = false;
    };

    const shimeDay = ref('20');

    const changeShimeDay = (() => {
      canDownLoad.value = false;
      isChecked.value = false;
    });

    const checkShimeStatus = (async () => {
      isChecked.value = true;
      canDownLoad.value = false;
      form.value.year = yearMonthForMonthPicker.value.year;
      form.value.month = yearMonthForMonthPicker.value.month + 1;
      form.value.shime = parseInt(shimeDay.value, 10);

      await getList(form);
      if (!isListError.value && checkListInfo.value.dakoku.length == 0 && checkListInfo.value.summary.length == 0) {
        canDownLoad.value = true;
      }

    });

    // CSVダウンロード処理
    const dlCsv = () => {
      let start = checkListInfo.value.start;
      let end = checkListInfo.value.end;
      downloadKintaiCSV(start, end);
    }

    return {
      form,
      isLoading,
      isCreating,
      isError,
      dlCsv,
      RouterConst,
      canDownLoad,
      yearMonth,
      shimeDay,
      yearMonthForMonthPicker,
      changeMonth,
      checkShimeStatus,
      changeShimeDay,
      isListChecking,
      isListError,
      getList,
      dateFormat,
      syouninStatus,
      isChecked,
      getError,
      checkListInfo,
    }
  },
  components: {

  }
})
</script>

<style lang="scss" scoped>
// @import '@scss/components/kinmu-list.scss';
// @import '@scss/components/koyoukeiyaku.scss';
@import '@scss/components/px.scss';
</style>
